import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useInjectReducer } from 'utils/redux-injectors';
import { 
  TemplateSettingState, 
  WidgetConfig, 
  SaveWidgetRequest, 
  GenerateCodeRequest, 
  GeneratedCode,
  IntegrationOption 
} from './types';
import { endpoints, formatErrors, baseQuery } from 'utils/api/endpoints';
import { ApiResponse } from 'types/ApiResponse';

export const initialState: TemplateSettingState = {
  widgetConfig: {
    buttonBgColor: '#8B5CF6', // Purple accent
    buttonText: 'View in your space',
    position: 'top-right',
    desktopPosition: 'top-right',
    mobilePosition: 'top-right',
  },
  previewMode: 'desktop',
  selectedOption: 'custom',
  isLoading: false,
  isSaving: false,
  isCopying: false,
  error: null,
  saveSuccess: false,
  copySuccess: false,
};

export const slice = createSlice({
  name: 'templateSetting',
  initialState,
  reducers: {
    setWidgetConfig(state, action: PayloadAction<WidgetConfig>) {
      state.widgetConfig = action.payload;
    },
    updateWidgetConfig(state, action: PayloadAction<Partial<WidgetConfig>>) {
      state.widgetConfig = { ...state.widgetConfig, ...action.payload };
    },
    setPreviewMode(state, action: PayloadAction<'desktop' | 'mobile'>) {
      state.previewMode = action.payload;
    },
    setSelectedOption(state, action: PayloadAction<IntegrationOption>) {
      state.selectedOption = action.payload;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setSaving(state, action: PayloadAction<boolean>) {
      state.isSaving = action.payload;
    },
    setCopying(state, action: PayloadAction<boolean>) {
      state.isCopying = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setSaveSuccess(state, action: PayloadAction<boolean>) {
      state.saveSuccess = action.payload;
    },
    setCopySuccess(state, action: PayloadAction<boolean>) {
      state.copySuccess = action.payload;
    },
    clearMessages(state) {
      state.error = null;
      state.saveSuccess = false;
      state.copySuccess = false;
    },
    resetToDefaults(state) {
      state.widgetConfig = initialState.widgetConfig;
    },
  },
});

export const { actions: templateSettingActions } = slice;

export const api = createApi({
  reducerPath: 'templateSettingApi',
  baseQuery,
  tagTypes: ['WidgetTemplate'],
  endpoints: build => ({
    getWidgetTemplate: build.query<ApiResponse<WidgetConfig>, void>({
      query: () => ({
        url: endpoints.getWidgetTemplate?.url || '/api/widget-template',
        method: endpoints.getWidgetTemplate?.method || 'GET',
      }),
      providesTags: ['WidgetTemplate'],
      transformErrorResponse(baseQueryReturnValue) {
        return formatErrors(baseQueryReturnValue.data);
      },
    }),
    saveWidgetTemplate: build.mutation<ApiResponse<WidgetConfig>, SaveWidgetRequest>({
      query: (data) => ({
        url: endpoints.saveWidgetTemplate?.url || '/api/widget-template',
        method: endpoints.saveWidgetTemplate?.method || 'POST',
        body: data,
      }),
      invalidatesTags: ['WidgetTemplate'],
      transformErrorResponse(baseQueryReturnValue) {
        return formatErrors(baseQueryReturnValue.data);
      },
    }),
    generateARButtonCode: build.mutation<ApiResponse<GeneratedCode>, GenerateCodeRequest>({
      query: (data) => ({
        url: endpoints.generateARButtonCode?.url || '/api/generate-ar-code',
        method: endpoints.generateARButtonCode?.method || 'POST',
        body: data,
      }),
      transformErrorResponse(baseQueryReturnValue) {
        return formatErrors(baseQueryReturnValue.data);
      },
    }),
  }),
});

export const useTemplateSettingSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  
  return {
    actions: templateSettingActions,
    // Template queries and mutations
    useGetWidgetTemplateQuery: api.useGetWidgetTemplateQuery,
    useLazyGetWidgetTemplateQuery: api.useLazyGetWidgetTemplateQuery,
    useSaveWidgetTemplateMutation: api.useSaveWidgetTemplateMutation,
    useGenerateARButtonCodeMutation: api.useGenerateARButtonCodeMutation,
  };
}; 